import { useEffect, useRef } from 'react';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Table from '@tiptap/extension-table';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TableRow from '@tiptap/extension-table-row';
import { EditorContent, useEditor, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Markdown } from 'tiptap-markdown';
import { useTranslation } from 'react-i18next';

interface MarkdownEditorProps {
  value: string;
  onChange: (markdown: string) => void;
  onOpenInternal?: (pageId: string) => void;
  placeholder?: string;
  autoFocus?: boolean;
  /** Read-only render (public view): no toolbar, content not editable. */
  editable?: boolean;
}

/**
 * Wrap bare http(s) URLs sitting in plain text into markdown links so they render
 * as clickable anchors. URLs already inside a `[text](url)` or `<url>` are left alone.
 */
function autolink(md: string): string {
  // kolejno: link z obrazkiem, sam obrazek, zwykły link, <url>, goły URL (grupa 1)
  const re =
    /\[!\[[^\]]*\]\([^)]*\)\]\([^)]*\)|!\[[^\]]*\]\([^)]*\)|\[[^\]]*\]\([^)]*\)|<https?:\/\/[^>]+>|(https?:\/\/[^\s<>()[\]]+)/g;
  return md.replace(re, (m, bare?: string) => {
    if (!bare) return m;
    const trail = bare.match(/[.,;:)\]]+$/)?.[0] ?? '';
    const url = trail ? bare.slice(0, -trail.length) : bare;
    return `[${url}](${url})${trail}`;
  });
}


const IMAGE_MAX_PX = 1600;
const IMAGE_QUALITY = 0.82;

/** Skaluje wklejony/upuszczony plik graficzny i zwraca go jako data URI. */
function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('read failed'));
    reader.onload = () => {
      const img = new window.Image();
      img.onerror = () => reject(new Error('decode failed'));
      img.onload = () => {
        const scale = Math.min(1, IMAGE_MAX_PX / Math.max(img.width, img.height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        const ctx = canvas.getContext('2d');
        if (!ctx) return reject(new Error('no canvas'));
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const type = file.type === 'image/png' && file.size < 400_000 ? 'image/png' : 'image/jpeg';
        resolve(canvas.toDataURL(type, IMAGE_QUALITY));
      };
      img.src = reader.result as string;
    };
    reader.readAsDataURL(file);
  });
}


/** Wstawia pliki graficzne w miejscu kursora jako obrazki (data URI). */
function insertImageFiles(view: { state: any; dispatch: (tr: any) => void }, files: File[]) {
  files.forEach(async (file) => {
    try {
      const src = await fileToDataUrl(file);
      const node = view.state.schema.nodes.image.create({ src });
      view.dispatch(view.state.tr.replaceSelectionWith(node));
    } catch {
      /* nieczytelny plik - pomijamy, użytkownik może wstawić obrazek z adresu */
    }
  });
}

/**
 * tiptap-markdown only serializes a table when it starts with a plain header row;
 * anything else (a copied fragment that begins mid-table, merged cells, a cell
 * holding more than one block) falls through to a stub that writes the literal
 * text `[table]`. That reached the clipboard on every partial-table copy, and
 * would reach the stored markdown for a table it considers irregular.
 *
 * This serializer always emits pipe rows instead: inline formatting is preserved
 * for single-block cells, richer cells collapse to their text, colspans expand
 * into empty cells, and the `---` delimiter is only written when the first row
 * really is a header (a fragment without one stays a bare set of rows).
 */
const MarkdownTable = Table.extend({
  addStorage() {
    return {
      ...this.parent?.(),
      markdown: {
        serialize(state: any, node: any) {
          const columnCount = (row: any): number => {
            let count = 0;
            row.forEach((cell: any) => {
              count += Math.max(1, cell.attrs?.colspan ?? 1);
            });
            return count;
          };

          let width = 0;
          node.forEach((row: any) => {
            width = Math.max(width, columnCount(row));
          });
          if (width === 0) {
            state.closeBlock(node);
            return;
          }

          const firstRow = node.firstChild;
          let headerRow = firstRow != null && firstRow.childCount > 0;
          firstRow?.forEach((cell: any) => {
            if (cell.type.name !== 'tableHeader') headerRow = false;
          });

          state.inTable = true;
          node.forEach((row: any, _offset: number, rowIndex: number) => {
            let written = 0;
            state.write('| ');
            row.forEach((cell: any, _cellOffset: number, cellIndex: number) => {
              if (cellIndex) state.write(' | ');
              if (cell.childCount === 1 && cell.firstChild?.isTextblock) {
                if (cell.firstChild.textContent.trim()) state.renderInline(cell.firstChild);
              } else if (cell.textContent.trim()) {
                // Multi-block cell: markdown rows are single-line, so flatten it.
                state.write(cell.textContent.replace(/\s+/g, ' ').trim().replace(/\|/g, '\\|'));
              }
              written += 1;
              for (let extra = 1; extra < Math.max(1, cell.attrs?.colspan ?? 1); extra += 1) {
                state.write(' | ');
                written += 1;
              }
            });
            for (let pad = written; pad < width; pad += 1) state.write(' | ');
            state.write(' |');
            state.ensureNewLine();
            if (rowIndex === 0 && headerRow) {
              state.write(`| ${Array.from({ length: width }, () => '---').join(' | ')} |`);
              state.ensureNewLine();
            }
          });
          state.closeBlock(node);
          state.inTable = false;
        },
        parse: {}
      }
    };
  }
});

/**
 * A "simpler than Notion" WYSIWYG editor. Content is edited richly but stored as
 * markdown (tiptap-markdown), which is what the knowledge pages persist.
 * Remount with a key when switching pages so the editor re-seeds from markdown.
 * Internal cross-page links use the `knowledge:<id>` href scheme and navigate
 * in-app; external http(s) links open in a new tab.
 */
export function MarkdownEditor({
  value,
  onChange,
  onOpenInternal,
  autoFocus,
  editable = true
}: MarkdownEditorProps) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const editor = useEditor({
    editable,
    autofocus: autoFocus ? 'end' : false,
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Link.configure({
        openOnClick: true,
        autolink: true,
        linkOnPaste: true,
        defaultProtocol: 'https',
        protocols: ['http', 'https', 'knowledge'],
        HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer' }
      }),
      Image.configure({ inline: true, allowBase64: true }),
      MarkdownTable.configure({ resizable: false }),
      TableRow,
      TableHeader,
      TableCell,
      Markdown.configure({ html: false, transformPastedText: true, transformCopiedText: true })
    ],
    content: autolink(value),
    editorProps: {
      handlePaste(view, event) {
        const files = Array.from(event.clipboardData?.files ?? []).filter((f) =>
          f.type.startsWith('image/')
        );
        if (files.length === 0) return false;
        event.preventDefault();
        insertImageFiles(view, files);
        return true;
      },
      handleDrop(view, event) {
        const dt = (event as DragEvent).dataTransfer;
        const files = Array.from(dt?.files ?? []).filter((f) => f.type.startsWith('image/'));
        if (files.length === 0) return false;
        event.preventDefault();
        insertImageFiles(view, files);
        return true;
      },
      handleClick(_view, _pos, event) {
        const target = event.target as HTMLElement;
        const anchor = target.closest('a');
        const href = anchor?.getAttribute('href') ?? '';
        if (href.startsWith('knowledge:')) {
          event.preventDefault();
          onOpenInternal?.(href.slice('knowledge:'.length));
          return true;
        }
        // klik w obrazek opakowany linkiem: ProseMirror zaznaczyłby węzeł, więc
        // otwieramy link sami
        if (href && target.tagName === 'IMG') {
          event.preventDefault();
          window.open(href, '_blank', 'noopener,noreferrer');
          return true;
        }
        return false;
      }
    },
    onUpdate: ({ editor: ed }) => onChange(ed.storage.markdown.getMarkdown())
  });

  // ProseMirror zjada klik w obrazek (zaznacza węzeł), więc link wokół obrazka
  // obsługujemy sami w fazie przechwytywania
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const onClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (target.tagName !== 'IMG') return;
      const href = target.closest('a')?.getAttribute('href');
      if (!href || href.startsWith('knowledge:')) return;
      event.preventDefault();
      event.stopPropagation();
      window.open(href, '_blank', 'noopener,noreferrer');
    };
    el.addEventListener('click', onClick, true);
    return () => el.removeEventListener('click', onClick, true);
  }, []);

  if (!editor) return <div className="md-editor md-editor-loading" ref={wrapRef} />;

  return (
    <div className="md-editor" ref={wrapRef}>
      {editable && <Toolbar editor={editor} />}
      <EditorContent editor={editor} className="md-content" />
    </div>
  );
}

function Toolbar({ editor }: { editor: Editor }) {
  const { t } = useTranslation();
  const btn = (active: boolean, label: string, title: string, onClick: () => void) => (
    <button
      type="button"
      className={`md-tool${active ? ' on' : ''}`}
      title={title}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
    >
      {label}
    </button>
  );

  return (
    <div className="md-toolbar">
      {btn(
        editor.isActive('heading', { level: 1 }),
        'H1',
        t('knowledge.h1'),
        () => editor.chain().focus().toggleHeading({ level: 1 }).run()
      )}
      {btn(
        editor.isActive('heading', { level: 2 }),
        'H2',
        t('knowledge.h2'),
        () => editor.chain().focus().toggleHeading({ level: 2 }).run()
      )}
      {btn(
        editor.isActive('heading', { level: 3 }),
        'H3',
        t('knowledge.h3'),
        () => editor.chain().focus().toggleHeading({ level: 3 }).run()
      )}
      <span className="md-sep" />
      {btn(editor.isActive('bold'), 'B', t('knowledge.bold'), () =>
        editor.chain().focus().toggleBold().run()
      )}
      {btn(editor.isActive('italic'), 'I', t('knowledge.italic'), () =>
        editor.chain().focus().toggleItalic().run()
      )}
      {btn(editor.isActive('strike'), 'S', t('knowledge.strike'), () =>
        editor.chain().focus().toggleStrike().run()
      )}
      <span className="md-sep" />
      {btn(editor.isActive('bulletList'), '•', t('knowledge.bullet'), () =>
        editor.chain().focus().toggleBulletList().run()
      )}
      {btn(editor.isActive('orderedList'), '1.', t('knowledge.ordered'), () =>
        editor.chain().focus().toggleOrderedList().run()
      )}
      {btn(editor.isActive('blockquote'), '"', t('knowledge.quote'), () =>
        editor.chain().focus().toggleBlockquote().run()
      )}
      {btn(editor.isActive('codeBlock'), '</>', t('knowledge.code'), () =>
        editor.chain().focus().toggleCodeBlock().run()
      )}
      <span className="md-sep" />
      {btn(editor.isActive('table'), '⊞', t('knowledge.table'), () =>
        editor.isActive('table')
          ? editor.chain().focus().deleteTable().run()
          : editor
              .chain()
              .focus()
              .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
              .run()
      )}
      {btn(editor.isActive('image'), '🖼', t('knowledge.image'), () => {
        const src = window.prompt(t('knowledge.imagePrompt'));
        if (src) editor.chain().focus().setImage({ src: src.trim() }).run();
      })}
    </div>
  );
}
